import { useState, useEffect, useCallback, useRef } from "react";
import { T, API, Icon } from "../AdminPanel";
import { useApi } from "../AdminPanel";

export default function ServicesEditorPage({ token, toast, onExpired }) {
  const { get, del, patch, upload } = useApi(token, onExpired);
  const addRef     = useRef();
  const replaceRef = useRef();

  const [services, setServices]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [editing, setEditing]           = useState(null);
  const [editForm, setEditForm]         = useState({});
  const [saving, setSaving]             = useState(false);
  const [uploading, setUploading]       = useState(false);
  const [replaceTarget, setReplaceTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await get("/images/?section=services");
      setServices([...data].sort((a, b) => a.sort_order - b.sort_order));
    } catch (e) {
      if (e.message !== "SESSION_EXPIRED") toast("Ошибка загрузки", "error");
    } finally {
      setLoading(false);
    }
  }, [get, toast]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (svc) => {
    setEditing(svc.id);
    setEditForm({ title: svc.title, description: svc.description || "", category: svc.category || "" });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await patch(`/images/${editing}/meta`, {
        title:       editForm.title,
        description: editForm.description,
        category:    editForm.category || null,
      });
      toast("Сохранено", "success");
      setEditing(null);
      load();
    } catch { toast("Ошибка сохранения", "error"); }
    finally   { setSaving(false); }
  };

  const replacePhoto = async (file, svc) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file",        file);
      fd.append("section",     "services");
      fd.append("title",       svc.title);
      fd.append("description", svc.description || "");
      fd.append("category",    svc.category || "");
      fd.append("sort_order",  String(svc.sort_order));
      await upload("/images/upload", fd);
      await del(`/images/${svc.id}`);
      toast("Фото заменено", "success");
      load();
    } catch { toast("Ошибка замены фото", "error"); }
    finally   { setUploading(false); setReplaceTarget(null); }
  };

  const addNew = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file",       file);
      fd.append("section",    "services");
      fd.append("title",      "Новая услуга");
      fd.append("description","");
      fd.append("sort_order", String(services.length));
      await upload("/images/upload", fd);
      toast("Услуга добавлена", "success");
      load();
    } catch { toast("Ошибка", "error"); }
    finally   { setUploading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await del(`/images/${id}`);
      toast("Удалено", "success");
      setServices(prev => prev.filter(s => s.id !== id));
    } catch { toast("Ошибка", "error"); }
  };

  const moveUp = async (idx) => {
    if (idx === 0) return;
    const a = services[idx], b = services[idx - 1];
    await Promise.all([
      patch(`/images/${a.id}/meta`, { sort_order: b.sort_order }),
      patch(`/images/${b.id}/meta`, { sort_order: a.sort_order }),
    ]);
    load();
  };

  const moveDown = async (idx) => {
    if (idx === services.length - 1) return;
    const a = services[idx], b = services[idx + 1];
    await Promise.all([
      patch(`/images/${a.id}/meta`, { sort_order: b.sort_order }),
      patch(`/images/${b.id}/meta`, { sort_order: a.sort_order }),
    ]);
    load();
  };

  return (
    <div>
      <div className="adm-eyebrow">Контент-менеджмент</div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 28 }}>
        <h1 style={{ fontFamily: "'Oswald',sans-serif", fontSize: 32, fontWeight: 400, textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Управление услугами
        </h1>
        <div style={{ display: "flex", gap: 8 }}>
          <input ref={addRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={e => e.target.files[0] && addNew(e.target.files[0])} />
          <input ref={replaceRef} type="file" accept="image/*" style={{ display: "none" }}
            onChange={e => { if (e.target.files[0] && replaceTarget) replacePhoto(e.target.files[0], replaceTarget); }} />
          <button className="adm-btn adm-btn--primary" onClick={() => addRef.current.click()} disabled={uploading}>
            {Icon.Plus} Добавить услугу
          </button>
          <button className="adm-btn" onClick={load}>{Icon.Refresh} Обновить</button>
        </div>
      </div>

      {loading ? (
        <div className="adm-empty"><span className="adm-spinner" /></div>
      ) : services.length === 0 ? (
        <div className="adm-empty">Нет услуг. Добавьте первую — она появится на сайте автоматически.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {services.map((svc, idx) => (
            <div key={svc.id} className="adm-card" style={{ padding: 0, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "200px 1fr" }}>
                {/* Превью */}
                <div style={{ position: "relative", background: "rgba(18,49,51,0.05)" }}>
                  <img src={`${API}/images/${svc.id}/file`} alt={svc.title}
                    style={{ width: "100%", height: "100%", minHeight: 140, objectFit: "cover", display: "block" }} />
                  <button
                    className="adm-btn adm-btn--sm"
                    style={{ position: "absolute", bottom: 8, left: 8, background: "rgba(18,49,51,0.7)", borderColor: "transparent", color: "white" }}
                    onClick={() => { setReplaceTarget(svc); replaceRef.current.click(); }}
                    disabled={uploading}
                  >
                    {Icon.Upload} Заменить
                  </button>
                </div>

                {/* Контент */}
                <div style={{ padding: "16px 20px" }}>
                  {editing === svc.id ? (
                    <div className="adm-form" style={{ gap: 10 }}>
                      <div className="adm-field">
                        <label className="adm-label">Заголовок</label>
                        <input className="adm-input" value={editForm.title}
                          onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} />
                      </div>
                      <div className="adm-field">
                        <label className="adm-label">Тег</label>
                        <input className="adm-input" value={editForm.category} placeholder="СРО, 24/7 и т.д."
                          onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))} />
                      </div>
                      <div className="adm-field">
                        <label className="adm-label">Описание</label>
                        <textarea className="adm-textarea" rows={3} value={editForm.description}
                          onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))} />
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="adm-btn adm-btn--primary adm-btn--sm" onClick={saveEdit} disabled={saving}>
                          {saving ? <span className="adm-spinner" /> : Icon.Check} Сохранить
                        </button>
                        <button className="adm-btn adm-btn--sm" onClick={() => setEditing(null)}>Отмена</button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                        <span style={{ fontFamily: "'Oswald',sans-serif", fontSize: 18, color: T.primary }}>{svc.title}</span>
                        {svc.category && (
                          <span style={{ padding: "2px 8px", background: T.yellow, color: T.primary, fontSize: 10, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                            {svc.category}
                          </span>
                        )}
                      </div>
                      {svc.description && (
                        <p style={{ fontSize: 13, color: T.textMuted, lineHeight: 1.6, margin: "0 0 12px" }}>{svc.description}</p>
                      )}
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="adm-btn adm-btn--sm" onClick={() => startEdit(svc)}>{Icon.Edit} Редактировать</button>
                        <button className="adm-btn adm-btn--sm" onClick={() => moveUp(idx)}   disabled={idx === 0}>↑</button>
                        <button className="adm-btn adm-btn--sm" onClick={() => moveDown(idx)} disabled={idx === services.length - 1}>↓</button>
                        <button className="adm-btn adm-btn--sm adm-btn--danger" onClick={() => handleDelete(svc.id)}>{Icon.Trash}</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}